import { useMemo, type ReactNode } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { format as formatDate } from 'date-fns';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import Typography from '@mui/material/Typography';
import { alpha, type Theme } from '@mui/material/styles';
import { DateRangePicker as RsDateRangePicker } from 'rsuite';

/**
 * Custom class applied to the rsuite popup so the layout overrides below stay
 * scoped to this component (rsuite CSS is global). Ships with the component via
 * MUI `GlobalStyles`, so consumers get the layout without extra setup.
 */
const POPUP_CLASS = 'octopus-daterange-popup';

/**
 * Re-flows rsuite's stacked panel (header on top, shortcuts + OK in a bottom
 * toolbar) into the product layout: a vertical preset rail on the left,
 * separated by a full-height divider from the calendar section on the right.
 * The calendar section's footer bar carries the selected range at its far-left
 * edge and the Apply button at its far-right edge.
 *
 * `display: contents` flattens rsuite's `content`/`toolbar` wrappers so their
 * children (header, calendars, ranges, ok button) become grid items of
 * `.rs-picker-box` and can be placed into named areas.
 */
const popupLayoutStyles = (theme: Theme) => {
  // `theme.palette.divider` is too faint against rsuite's dark popup, so use a
  // higher-contrast, theme-adaptive line for the section separators.
  const separator = `1px solid ${alpha(theme.palette.text.primary, 0.16)}`;
  return {
  [`.${POPUP_CLASS} .rs-picker-box`]: {
    display: 'grid',
    gridTemplateColumns: '168px minmax(0, 1fr)',
    gridTemplateRows: 'auto auto',
    // Rail spans both rows so its divider runs the full height; the calendar
    // section stacks the calendars above a full-width footer bar.
    gridTemplateAreas: '"rail calendars" "rail footer"',
  },
  [`.${POPUP_CLASS} .rs-picker-daterange-content`]: { display: 'contents' },
  [`.${POPUP_CLASS} .rs-picker-toolbar`]: { display: 'contents' },
  [`.${POPUP_CLASS} .rs-picker-daterange-calendar-group`]: { gridArea: 'calendars' },
  [`.${POPUP_CLASS} .rs-picker-toolbar-ranges`]: {
    gridArea: 'rail',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 2,
    margin: 0,
    padding: 8,
    borderRight: separator,
    overflowY: 'auto',
  },
  [`.${POPUP_CLASS} .rs-picker-toolbar-ranges .rs-btn`]: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '6px 12px',
    borderRadius: theme.shape.borderRadius,
  },
  // Apply pinned to the far-right edge of the calendar section's footer.
  [`.${POPUP_CLASS} .rs-picker-toolbar-right`]: {
    gridArea: 'footer',
    justifySelf: 'end',
    alignSelf: 'center',
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    padding: '10px 12px',
  },
  // Match the Button atom (contained/primary): same tokens as the MUI theme
  // `MuiButton` override so the Apply action is visually consistent.
  [`.${POPUP_CLASS} .rs-picker-toolbar-right .rs-btn`]: {
    minWidth: 64,
    padding: '6px 16px',
    borderRadius: theme.shape.borderRadius,
    border: 'none',
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.75,
    textTransform: 'none' as const,
    boxShadow: 'none',
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    transition: theme.transitions.create(['background-color', 'box-shadow']),
    '&:hover, &:focus-visible': {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.dark,
      boxShadow: 'none',
    },
    '&:active': { backgroundColor: theme.palette.primary.dark },
    '&:disabled, &.rs-btn-disabled': {
      color: theme.palette.action.disabled,
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
  // Selected range at the far-left edge of the calendar section's footer. It
  // stretches across the footer so its top border forms the full-width divider;
  // the Apply button sits in the same cell, pinned right.
  [`.${POPUP_CLASS} .rs-picker-daterange-header`]: {
    gridArea: 'footer',
    justifySelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    margin: 0,
    padding: '10px 12px',
    borderTop: separator,
    borderBottom: 'none',
  },
  };
};

/**
 * DateRangePicker
 * Classification: custom (wraps rsuite `DateRangePicker`)
 * rsuite base: https://rsuitejs.com/components/date-range-picker/
 *
 * A start/end date range picker built on rsuite's `DateRangePicker` (the same
 * control used in the product app), so the calendars, preset rail, time panel
 * with the "Now" shortcut and AM/PM toggle come straight from rsuite. This
 * wrapper keeps the ergonomic `@aistrike-dev/ui` surface: dayjs-friendly presets and
 * `valueType` conversion for `value`/`onChange`.
 *
 * Layout: preset shortcuts are arranged in a left rail; the footer bar carries
 * an Apply button (styled to match the Button atom) pinned to the left edge and
 * the selected range aligned to the right edge.
 *
 * Full-day default: the working times default to 12:00:00 AM (start) and
 * 11:59:59 PM (end), so picking the same day twice yields a range that spans the
 * whole day (00:00:00 → 23:59:59). Times set explicitly in the time panel are
 * preserved.
 *
 * rsuite is themed by the consumer (via `<CustomProvider>` / its CSS). Import
 * `rsuite/dist/rsuite-no-reset.min.css` once at the app root; Storybook does
 * this in `.storybook/preview.tsx`.
 */
export type DateRangeValueType = 'dayjs' | 'date' | 'string';

export type RangeBound = Dayjs | Date | string | null;

export interface DateRange<T = RangeBound> {
  start: T;
  end: T;
}

export interface DateRangePreset {
  label: string;
  getRange: () => { start: Dayjs | null; end: Dayjs | null };
}

export interface DateRangePickerProps {
  /** Controlled value. */
  value?: DateRange;
  /** Uncontrolled initial value. */
  defaultValue?: DateRange;
  /** Called with the committed range (converted to `valueType`). */
  onChange?: (range: DateRange) => void;
  /** Preset shortcuts. Defaults to a canonical list. */
  presets?: DateRangePreset[];
  /** Label of the preset to select initially. */
  defaultPreset?: string;
  /** Allow clearing the range (emits `{ start: null, end: null }`). */
  allowAllTime?: boolean;
  /** Bias default presets toward future dates. */
  futureMode?: boolean;
  minDate?: RangeBound;
  maxDate?: RangeBound;
  /** Output value type for `onChange`/`value`. Default `'dayjs'`. */
  valueType?: DateRangeValueType;
  /** Display + string-value format (date-fns tokens). Default `'MMM d, yyyy'`. */
  format?: string;
  /** Show the time panel (hh:mm:ss + Now). Default `true`. */
  showTime?: boolean;
  /** 12-hour clock with an AM/PM toggle in the time panel. Default `true`. */
  showMeridian?: boolean;
  /** `standalone` renders an inline trigger; `embedded` renders a full-width block with a label. */
  variant?: 'standalone' | 'embedded';
  disabled?: boolean;
  label?: string;
  /** Overlay placement (rsuite). */
  placement?: React.ComponentProps<typeof RsDateRangePicker>['placement'];
}

type RsRange = [Date, Date];

const toDate = (v: RangeBound): Date | null => {
  if (v === null || v === undefined) return null;
  const d = dayjs(v as dayjs.ConfigType);
  return d.isValid() ? d.toDate() : null;
};

const toRsRange = (range: DateRange | undefined): RsRange | null => {
  if (!range) return null;
  const start = toDate(range.start);
  const end = toDate(range.end);
  return start && end ? [start, end] : null;
};

function fromDate(d: Date | null, valueType: DateRangeValueType, fmt: string): RangeBound {
  if (!d) return null;
  switch (valueType) {
    case 'date':
      return d;
    case 'string':
      return formatDate(d, fmt);
    default:
      return dayjs(d);
  }
}

function buildDefaultPresets(futureMode: boolean): DateRangePreset[] {
  const today = () => dayjs().startOf('day');
  return futureMode
    ? [
        { label: 'Today', getRange: () => ({ start: today(), end: dayjs().endOf('day') }) },
        { label: 'Next 7 days', getRange: () => ({ start: today(), end: today().add(6, 'day').endOf('day') }) },
        { label: 'Next 30 days', getRange: () => ({ start: today(), end: today().add(29, 'day').endOf('day') }) },
        { label: 'Next 90 days', getRange: () => ({ start: today(), end: today().add(89, 'day').endOf('day') }) },
        { label: 'This month', getRange: () => ({ start: dayjs().startOf('month'), end: dayjs().endOf('month') }) },
      ]
    : [
        { label: 'Today', getRange: () => ({ start: today(), end: dayjs().endOf('day') }) },
        { label: 'Last 7 days', getRange: () => ({ start: today().subtract(6, 'day'), end: dayjs().endOf('day') }) },
        { label: 'Last 30 days', getRange: () => ({ start: today().subtract(29, 'day'), end: dayjs().endOf('day') }) },
        { label: 'Last 90 days', getRange: () => ({ start: today().subtract(89, 'day'), end: dayjs().endOf('day') }) },
        { label: 'This month', getRange: () => ({ start: dayjs().startOf('month'), end: dayjs().endOf('month') }) },
      ];
}

export function DateRangePicker({
  value,
  defaultValue,
  onChange,
  presets,
  defaultPreset,
  allowAllTime = false,
  futureMode = false,
  minDate,
  maxDate,
  valueType = 'dayjs',
  format = 'MMM d, yyyy',
  showTime = true,
  showMeridian = true,
  variant = 'standalone',
  disabled = false,
  label,
  placement = 'bottomStart',
}: DateRangePickerProps) {
  const resolvedPresets = useMemo(
    () => presets ?? buildDefaultPresets(futureMode),
    [presets, futureMode],
  );

  // rsuite renders the time panel only when the format carries time tokens.
  const displayFormat = useMemo(() => {
    if (!showTime) return format;
    return `${format} ${showMeridian ? 'hh:mm:ss aa' : 'HH:mm:ss'}`;
  }, [format, showTime, showMeridian]);

  const ranges = useMemo(
    () =>
      resolvedPresets
        .map((preset) => {
          const range = preset.getRange();
          const start = range.start?.toDate();
          const end = range.end?.toDate();
          return start && end ? { label: preset.label, value: [start, end] as RsRange } : null;
        })
        .filter((r): r is { label: string; value: RsRange } => r !== null),
    [resolvedPresets],
  );

  const shouldDisableDate = useMemo(() => {
    const minD = toDate(minDate ?? null);
    const maxD = toDate(maxDate ?? null);
    const predicates = [
      minD ? RsDateRangePicker.before(minD) : null,
      maxD ? RsDateRangePicker.after(maxD) : null,
    ].filter((p): p is NonNullable<typeof p> => p !== null);
    if (predicates.length === 0) return undefined;
    return RsDateRangePicker.combine(...predicates);
  }, [minDate, maxDate]);

  const isControlled = value !== undefined;

  const controlledValue = useMemo(() => (isControlled ? toRsRange(value) : undefined), [isControlled, value]);

  const defaultRsValue = useMemo(() => {
    if (defaultValue) return toRsRange(defaultValue);
    if (defaultPreset) {
      const preset = resolvedPresets.find((p) => p.label === defaultPreset);
      if (preset) {
        const range = preset.getRange();
        const start = range.start?.toDate();
        const end = range.end?.toDate();
        if (start && end) return [start, end] as RsRange;
      }
    }
    return null;
  }, [defaultValue, defaultPreset, resolvedPresets]);

  // Default the working times so a start defaults to 00:00:00 and an end to
  // 23:59:59 — picking the same day twice then covers the whole day. The dates
  // here only seed the two visible months (this month + next); the day is
  // replaced on click, only the time-of-day is carried over.
  const defaultCalendarValue = useMemo<RsRange>(() => {
    const base = dayjs();
    return [base.startOf('day').toDate(), base.add(1, 'month').endOf('day').toDate()];
  }, []);

  const handleChange = (next: RsRange | null) => {
    if (!next) {
      onChange?.({ start: null, end: null });
      return;
    }
    let start = dayjs(next[0]);
    let end = dayjs(next[1]);
    // Same-day selection with default (equal) times: cover the whole day so a
    // "same day twice" pick spans 00:00:00 → 23:59:59. Distinct times set via
    // the time panel are preserved.
    if (start.isSame(end, 'day') && start.format('HH:mm:ss') === end.format('HH:mm:ss')) {
      start = start.startOf('day');
      end = end.endOf('day');
    }
    onChange?.({
      start: fromDate(start.toDate(), valueType, displayFormat),
      end: fromDate(end.toDate(), valueType, displayFormat),
    });
  };

  const picker: ReactNode = (
    <RsDateRangePicker
      value={controlledValue}
      defaultValue={defaultRsValue}
      defaultCalendarValue={defaultCalendarValue}
      onChange={handleChange}
      ranges={ranges}
      format={displayFormat}
      showMeridiem={showMeridian}
      shouldDisableDate={shouldDisableDate}
      disabled={disabled}
      cleanable={allowAllTime}
      placeholder={label ?? 'Select range'}
      placement={placement}
      character=" – "
      popupClassName={POPUP_CLASS}
      locale={{ ok: 'Apply' }}
      block={variant === 'embedded'}
    />
  );

  if (variant === 'embedded') {
    return (
      <Box>
        <GlobalStyles styles={popupLayoutStyles} />
        {label ? (
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {label}
          </Typography>
        ) : null}
        {picker}
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles styles={popupLayoutStyles} />
      {picker}
    </>
  );
}
